import type { CollectionConfig } from '../types/collections';

/**
 * Render-time subtitle selection for picker rows.
 *
 * Pickers render only the collection's `display_template` (People's is
 * `{first name}`), so two "Daniel"s are indistinguishable. This module picks
 * 1–2 populated "differentiating" fields per row — client-side, at render
 * time, from the schema the row already carries — and formats them as a
 * subtitle (`value` or `value · value2`). No new collection config involved.
 */

/** The minimal row shape the subtitle logic needs. */
export interface SubtitleRow {
  id: number | string;
  data: Record<string, any>;
}

/** Same placeholder syntax as `renderCollectionItem`. */
const TEMPLATE_PLACEHOLDER = /\{([^}]+)\}/g;

/** Field names referenced by the display template (they'd be redundant in a subtitle). */
function templateFieldNames(config: CollectionConfig): Set<string> {
  const names = new Set<string>();
  for (const match of config.display_template.matchAll(TEMPLATE_PLACEHOLDER)) {
    names.add(match[1]);
  }
  return names;
}

/**
 * Candidate subtitle fields: schema fields ordered by `position`, minus
 * fields already shown by the display template, minus picture fields.
 */
function candidateFieldNames(config: CollectionConfig): string[] {
  const excluded = templateFieldNames(config);
  return Object.entries(config.fields ?? {})
    .filter(([name, field]) => !excluded.has(name) && field.type !== 'picture')
    .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
    .map(([name]) => name);
}

function isPopulated(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
}

/** Format a field value for subtitle display (mirrors `renderCollectionItem`'s coercion). */
function formatValue(value: any, config: CollectionConfig, fieldName: string): string {
  const field = config.fields?.[fieldName];
  if (field?.type === 'boolean') return value ? 'Yes' : 'No';
  return String(value).trim();
}

/** The primary text the picker shows for this row (display_template render). */
function renderDisplay(data: Record<string, any>, config: CollectionConfig): string {
  return config.display_template
    .replace(TEMPLATE_PLACEHOLDER, (_match, fieldName) => {
      const value = data[fieldName];
      if (value === null || value === undefined) return '';
      return formatValue(value, config, fieldName);
    })
    .trim();
}

/** Normalized comparison key so "Coach"/"coach " count as the same value. */
function comparisonKey(value: any, config: CollectionConfig, fieldName: string): string {
  if (!isPopulated(value)) return '';
  return formatValue(value, config, fieldName).toLowerCase();
}

/** First populated candidate field's value for a row, or '' if none. */
function firstPopulatedSubtitle(
  row: SubtitleRow,
  candidates: string[],
  config: CollectionConfig,
): string {
  for (const name of candidates) {
    if (isPopulated(row.data?.[name])) {
      return formatValue(row.data[name], config, name);
    }
  }
  return '';
}

/**
 * For a group of rows whose primary display text collides, pick up to 2
 * candidate fields (in `position` order) that are populated somewhere in the
 * group and whose values actually differ across it. A second field is added
 * only when the first doesn't fully distinguish the group.
 */
function differentiatingFields(
  group: SubtitleRow[],
  candidates: string[],
  config: CollectionConfig,
): string[] {
  const qualifying = candidates.filter((name) => {
    const keys = group.map((row) => comparisonKey(row.data?.[name], config, name));
    const populatedSomewhere = group.some((row) => isPopulated(row.data?.[name]));
    return populatedSomewhere && new Set(keys).size > 1;
  });

  if (qualifying.length === 0) return [];

  const [first, ...rest] = qualifying;
  const firstKeys = group.map((row) => comparisonKey(row.data?.[first], config, first));
  const fullyDistinct = new Set(firstKeys).size === group.length;
  return fullyDistinct || rest.length === 0 ? [first] : [first, rest[0]];
}

/**
 * Compute a subtitle per displayed picker row.
 *
 * Rows whose rendered display text collides with another displayed row get up
 * to 2 populated fields whose values differ within the colliding set;
 * non-colliding rows (and colliding rows where nothing differs) fall back to
 * their first populated candidate field. Values joined with " · "; rows with
 * nothing to show map to ''.
 */
export function computeSubtitles(
  rows: SubtitleRow[],
  config: CollectionConfig,
): Map<number | string, string> {
  const candidates = candidateFieldNames(config);
  const subtitles = new Map<number | string, string>();

  const groups = new Map<string, SubtitleRow[]>();
  for (const row of rows) {
    const display = renderDisplay(row.data ?? {}, config).toLowerCase();
    const group = groups.get(display);
    if (group) group.push(row);
    else groups.set(display, [row]);
  }

  for (const group of groups.values()) {
    const fields = group.length > 1 ? differentiatingFields(group, candidates, config) : [];
    for (const row of group) {
      const parts = fields
        .filter((name) => isPopulated(row.data?.[name]))
        .map((name) => formatValue(row.data[name], config, name));
      const subtitle = parts.length > 0
        ? parts.join(' · ')
        : firstPopulatedSubtitle(row, candidates, config);
      subtitles.set(row.id, subtitle);
    }
  }

  return subtitles;
}
